import styled from '@emotion/styled'
import gql from 'graphql-tag'
import React, { useContext } from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'
import { useQuery } from '../../../../api/graphql'
import { Hugeicon } from '../../../../icons/Hugeicon'
import { CurrentUserContext } from '../../../../providers/CurrentUserContext'
import Routes from '../../../../startup/routes'
import CalendarPromoCard from '../organizations/CalendarPromoCard'

const HAS_CALENDAR_QUERY = gql`
  query HasBusinessCalendar {
    currentUser {
      id
      organizationMemberships {
        organization {
          id
          hasActiveSubscription
        }
      }
    }
  }
`

type CalendarQueryResponse = {
  currentUser: {
    id: string
    organizationMemberships: Array<{
      organization: {
        id: string
        hasActiveSubscription: boolean
      }
    }>
  } | null
}

const DashWelcome: React.FC = () => {
  const { currentUser } = useContext(CurrentUserContext)
  const canSubmitStories = currentUser?.isAuthor
  const { data: calendarData } = useQuery<CalendarQueryResponse>(HAS_CALENDAR_QUERY)
  const hasActiveSubscription = calendarData?.currentUser?.organizationMemberships?.some(
    m => m.organization.hasActiveSubscription
  )

  return (
    <PageWrap>
      <Helmet>
        <title>Welcome</title>
      </Helmet>

      <PageHeader>
        <PageTitle>Dashboard</PageTitle>
      </PageHeader>

      <ContentArea>
        <CardRow>
          <ActionCard>
            <CardIcon name="calendar" size={56} style={{ color: '#d95859' }} />
            <CardLabel>Feature your event on the community calendar</CardLabel>
            <CardButton to={Routes.newEvent()} variant="coral">
              Submit an Event
            </CardButton>
          </ActionCard>

          <ActionCard>
            <CardIcon name="location" size={56} style={{ color: '#6fbf53' }} />
            <CardLabel>Include your business in the map</CardLabel>
            <CardButton to={Routes.newPlace()} variant="green">
              Submit a Place
            </CardButton>
          </ActionCard>

          {canSubmitStories && (
            <ActionCard>
              <CardIcon name="megaphone" size={56} style={{ color: '#3fb9b5' }} />
              <CardLabel>Write an article to share with your neighbors</CardLabel>
              <CardButton to={Routes.newStory()} variant="teal">
                Submit a Story
              </CardButton>
            </ActionCard>
          )}
        </CardRow>

        <CalendarPromoCard
          title="New from CivicLift: A Calendar for your Website!"
          ctaText={hasActiveSubscription ? 'Manage your calendar' : 'Get your calendar'}
          ctaHref="/dash/business-calendar"
        />
      </ContentArea>
    </PageWrap>
  )
}

export default DashWelcome

/* ── Layout ── */

const PageWrap = styled.section`
  min-height: 100vh;
  background: #f5f7fa;
`

const PageHeader = styled.header`
  padding: 32px 40px 24px;
`

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a2b3d;
  margin: 0;
  letter-spacing: -0.02em;
`

const ContentArea = styled.div`
  padding: 0 40px 48px;
  max-width: 823px;
`

/* ── Card Row ── */

const CardRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
`

const ActionCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: white;
  border-radius: 14px;
  padding: 28px 20px 24px;
  border: 1px solid #e8ecf1;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
  }
`

const CardIcon = styled(Hugeicon)`
  flex-shrink: 0;
  margin-bottom: 14px;
`

const CardLabel = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: #4b5c6f;
  margin: 0 0 18px;
  flex: 1;
`

const BUTTON_COLORS = {
  coral: { bg: '#e85d4a', hover: '#d44d3b' },
  green: { bg: '#4caf6a', hover: '#3d9a5a' },
  teal: { bg: '#3bb5b0', hover: '#2da19c' },
} as const

type Variant = keyof typeof BUTTON_COLORS

const CardButton = styled(Link)<{ variant: Variant }>`
  display: inline-block;
  padding: 10px 22px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  text-decoration: none;
  color: white;
  background: ${({ variant }) => BUTTON_COLORS[variant].bg};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ variant }) => BUTTON_COLORS[variant].hover};
    color: white;
  }
`
